import { createSelector } from '@reduxjs/toolkit';
import { getMediaMultiLang, getTextMultiLang } from '@utils/helper';
import { selectDomain, selectIsFreeOwnerPlan } from './mainSelectors';
import { getGroups } from '../utils';
import {
  selectDetailProject,
  selectLanguageProjectSelected,
} from './detailProjectSelectors';
import { selectDefaultLanguageProject } from './multiLanguageSelector';
import { I_Scene } from '../types/I_Scene';
import { TypeActionInteraction } from '../types/I_ConfigProject';
import { withSceneView } from '../view/sceneView';

const FREE_PLAN_HIDDEN_HOTSPOT_TYPES = new Set<TypeActionInteraction>([
  'instructor',
  'compact',
  'product',
  'patch',
]);

export const selectIsFirstScene = createSelector(
  [selectDomain],
  state => state.isFirstScene,
);

export const selectListScene = createSelector(
  [
    selectDomain,
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
    selectIsFreeOwnerPlan,
  ],
  (
    state,
    project,
    languageProjectSelected,
    defaultLanguageProject,
    isFreeOwnerPlan,
  ) =>
    (state.listScene || [])
      .filter(
        item => item.media || (item.type === 'object360' && item.object360),
      )
      .map(scene => {
        const sceneWithView =
          withSceneView(scene, project?.config?.setting?.view) ?? scene;
        const keyTranslations = project?.keyTranslations;
        const sourceHotspots = scene.hotspots || [];
        const hotspots: I_Scene['hotspots'] = [];
        const hotspotLen = sourceHotspots.length;
        for (let i = 0; i < hotspotLen; i++) {
          const hotspot = sourceHotspots[i];
          if (
            isFreeOwnerPlan &&
            FREE_PLAN_HIDDEN_HOTSPOT_TYPES.has(hotspot.type)
          )
            continue;
          hotspots.push({
            ...hotspot,
            title:
              getTextMultiLang(
                hotspot.title,
                keyTranslations,
                languageProjectSelected,
                defaultLanguageProject,
              ) || '',
            itemPrd: hotspot.itemPrd
              ? {
                  ...hotspot.itemPrd,
                  name:
                    getTextMultiLang(
                      hotspot.itemPrd?.name,
                      keyTranslations,
                      languageProjectSelected,
                      defaultLanguageProject,
                    ) || '',
                  buttonText: getTextMultiLang(
                    hotspot.itemPrd?.buttonText,
                    keyTranslations,
                    languageProjectSelected,
                    defaultLanguageProject,
                  ),
                  currency: getTextMultiLang(
                    hotspot.itemPrd?.currency,
                    keyTranslations,
                    languageProjectSelected,
                    defaultLanguageProject,
                  ),
                  price:
                    getTextMultiLang(
                      hotspot.itemPrd?.price,
                      keyTranslations,
                      languageProjectSelected,
                      defaultLanguageProject,
                    ) || '',
                }
              : undefined,
            config: {
              ...hotspot.config,
              style: {
                ...hotspot.config?.style,
                callout: hotspot.config?.style?.callout
                  ? {
                      ...hotspot.config?.style?.callout,
                      text: getTextMultiLang(
                        hotspot.config?.style?.callout?.text,
                        keyTranslations,
                        languageProjectSelected,
                        defaultLanguageProject,
                      ),
                    }
                  : undefined,
              },
              image: {
                ...hotspot.config?.image,
                galleries: hotspot.config?.image?.galleries?.map(media => ({
                  ...media,
                  name:
                    getTextMultiLang(
                      media.name,
                      keyTranslations,
                      languageProjectSelected,
                      defaultLanguageProject,
                    ) || '',
                })),
              },
              media: {
                ...hotspot.config?.media,
                content:
                  getTextMultiLang(
                    hotspot?.config?.media?.content || '',
                    keyTranslations,
                    languageProjectSelected,
                    defaultLanguageProject,
                  ) || '',
              },
              compact: hotspot.config?.compact?.map(item =>
                item.type === 'image'
                  ? {
                      ...item,
                      config: {
                        ...item.config,
                        galleries: item.config?.galleries?.map(
                          mediaCompact => ({
                            ...mediaCompact,
                            name:
                              getTextMultiLang(
                                mediaCompact.name,
                                keyTranslations,
                                languageProjectSelected,
                                defaultLanguageProject,
                              ) || '',
                          }),
                        ),
                      },
                    }
                  : item,
              ),
            },
          });
        }

        return {
          ...sceneWithView,
          title:
            getTextMultiLang(
              scene.title,
              keyTranslations,
              languageProjectSelected,
              defaultLanguageProject,
            ) || '',
          background_sound_multi_lang: getMediaMultiLang(
            scene.background_sound_multi_lang as unknown as string,
            keyTranslations,
            languageProjectSelected,
            defaultLanguageProject,
          ),
          staging: isFreeOwnerPlan
            ? []
            : scene.staging?.map(item => ({
                ...item,
                title:
                  getTextMultiLang(
                    item.title || '',
                    keyTranslations,
                    languageProjectSelected,
                    defaultLanguageProject,
                  ) || '',
              })),
          day2dust: isFreeOwnerPlan
            ? []
            : scene.day2dust?.map(item => ({
                ...item,
                title:
                  getTextMultiLang(
                    item.title || '',
                    keyTranslations,
                    languageProjectSelected,
                    defaultLanguageProject,
                  ) || '',
              })),
          hotspots,
          ...(isFreeOwnerPlan
            ? {
                mode:
                  sceneWithView.mode === 'staging' ||
                  sceneWithView.mode === 'day2dust'
                    ? ''
                    : sceneWithView.mode,
                configStaging: undefined,
                configPlugin: {
                  ...sceneWithView.configPlugin,
                  lensflare: {
                    ...sceneWithView.configPlugin?.lensflare,
                    enabled: false,
                  },
                },
              }
            : null),
        };
      }) as I_Scene[],
);

export const selectListGroup = createSelector(
  [
    selectListScene,
    selectDetailProject,
    selectLanguageProjectSelected,
    selectDefaultLanguageProject,
  ],
  (scenes, project, languageProjectSelected, defaultLanguageProject) => {
    const groups = getGroups(scenes, project?.groups)?.map(item => ({
      ...item,
      name:
        getTextMultiLang(
          item.name,
          project?.keyTranslations,
          languageProjectSelected,
          defaultLanguageProject,
        ) || '',
    }));
    return groups.length === 1 && groups[0].id === 'uncategory'
      ? groups
      : groups.filter(item => item.id !== 'uncategory');
  },
);

export const selectListSceneByGroup = createSelector(
  [selectListGroup],
  listGroup =>
    listGroup
      .reduce((result: I_Scene[][], item) => {
        result.push(item?.scenes || []);
        return result;
      }, [])
      .flat(),
);

export const selectSceneSelected = createSelector(
  [selectDomain, selectListScene, selectDetailProject],
  (state, scenes, project) => {
    if (!state.sceneSelected?.id) return undefined;
    const sceneFromList = scenes.find(
      item => item.id === state.sceneSelected?.id,
    );
    if (!sceneFromList) return undefined;
    return withSceneView(
      {
        ...sceneFromList,
        _id: state.sceneSelected._id,
        config:
          state.sceneSelected?.config !== undefined
            ? state.sceneSelected.config
            : sceneFromList.config,
      },
      project?.config?.setting?.view,
    ) as I_Scene;
  },
);

export const selectPrevSceneSelected = createSelector(
  [selectDomain],
  state => state.prevSceneSelected || undefined,
);

export const selectHotspotSelected = createSelector(
  [selectDomain],
  state => state.hotspotSelected || undefined,
);

export const selectDetailFirstScene = createSelector(
  [selectDetailProject, selectListScene, selectListGroup],
  (project, listScene, listGroup) =>
    project?.config?.setting?.general?.first_scene
      ? listScene.filter(
          item => item.id === project?.config?.setting?.general?.first_scene,
        )[0] || listGroup[0]?.scenes?.[0]
      : listGroup.length && listGroup[0]?.scenes?.length
      ? listGroup[0]?.scenes[0]
      : listScene[0],
);
